import { GetProductDto } from "src/services/generated-api";

/**
 * Four columns exist on the API's product but not yet in the generated
 * client (see the identical comment in single-product.tsx, which re-exports
 * this type rather than duplicating it — that file and this one both need
 * it, and this one has no dependency on that one, so it lives here).
 */
export type ProductWithAttributes = GetProductDto & {
  rank?: number;
  capacity?: number | null;
  photoSize?: string | null;
  createdAt?: string;
};

/**
 * Parsed product information extracted from product name
 */
export interface ParsedProductInfo {
  productId: string;
  quantity?: number;
  photoSize?: string;
  originalName: string;
  sizeMatches?: boolean; // Whether the product size matches user's photo size
}

/**
 * Parses a product name to extract quantity and photo size
 * Example: "Vijolični trenutki 40/10x15" -> { quantity: 40, photoSize: "10x15" }
 *
 * Supports various formats:
 * - "Product Name 40/10x15" (quantity/photosize)
 * - "Product Name 40 / 10x15" (with spaces)
 * - "Product Name 10x15/200" (photosize/quantity)
 * - "Elements 200/10x15" (quantity/photosize)
 * - "Product Name 10x15" (photosize only)
 * - "Product Name 40" (quantity only)
 */
export function parseProductName(name: string): {
  quantity?: number;
  photoSize?: string;
} {
  // Pattern 1: number/photosize (e.g., "40/10x15" or "Elements 200/10x15")
  // Accepts × (U+00D7) as well as x — several products are named e.g.
  // "30×31/100 Aura Sivi" — matching normalizePhotoSize below and the
  // backend's backfill migration, which both already accept [xX×].
  const quantityThenSizePattern = /(\d+)\s*\/\s*(\d+\s*[xX×]\s*\d+)/i;
  const match1 = name.match(quantityThenSizePattern);

  if (match1) {
    const result = {
      quantity: parseInt(match1[1], 10),
      photoSize: match1[2].toLowerCase().replace(/\s+/g, "").replace("×", "x"),
    };
    console.log(`[parseProductName] "${name}" → qty/size: ${result.quantity}/${result.photoSize}`);
    return result;
  }

  // Pattern 2: photosize/number (e.g., "10x15/200")
  const sizeThenQuantityPattern = /(\d+\s*[xX×]\s*\d+)\s*\/\s*(\d+)/i;
  const match2 = name.match(sizeThenQuantityPattern);

  if (match2) {
    const result = {
      photoSize: match2[1].toLowerCase().replace(/\s+/g, "").replace("×", "x"),
      quantity: parseInt(match2[2], 10),
    };
    console.log(`[parseProductName] "${name}" → size/qty: ${result.photoSize}/${result.quantity}`);
    return result;
  }

  // Pattern 3: just photo size (e.g., "10x15", "13x18")
  //
  // Excludes comma-decimal dimensions, e.g. "Pike AM 100/11,4x15,2"
  // (11.4cm x 15.2cm) or "Whale Pink 28x30,5/50" (28cm x 30.5cm). Without
  // this guard, `\d+\s*[xX×]\s*\d+` matches a bare fragment of the decimal —
  // "4x15" out of "11,4x15,2" — which is wrong data, not missing data.
  // Mirrors the same guard in the backend's BackfillProductAttributes
  // migration, which leaves these products' columns null for an admin to
  // fill in rather than guess.
  const sizeOnlyPattern = /(\d+\s*[xX×]\s*\d+)/i;
  const commaDecimalGuard = /\d,\d+\s*[xX×]|[xX×]\s*\d+,\d/i;
  const sizeMatch = name.match(sizeOnlyPattern);

  if (sizeMatch) {
    if (commaDecimalGuard.test(name)) {
      // Do NOT fall through to Pattern 4 here. Falling through used to parse
      // "Whale Pink 28x30,5/50" as `{quantity: 50}` with no size — the
      // trailing digits after the decimal comma — which let a 28x30.5cm
      // baby album match `getRelevantProducts` for *any* photo size (no
      // photoSize means it's never rejected by the exact-size check) and
      // render a false "fits your N photos" chip. Returning nothing here
      // instead makes these names parse to nothing on both frontend and
      // backend, matching their null capacity/photoSize columns.
      console.log(`[parseProductName] "${name}" → comma-decimal guard tripped, no match`);
      return {};
    }
    const result = {
      photoSize: sizeMatch[1].toLowerCase().replace(/\s+/g, "").replace("×", "x"),
    };
    console.log(`[parseProductName] "${name}" → size only: ${result.photoSize}`);
    return result;
  }

  // Pattern 4: just quantity at the end (must be meaningful quantity, not product number)
  // Only match if the number is >= 10 to avoid matching product series numbers like "Album 1"
  const quantityOnlyPattern = /(\d{2,})\s*$/;
  const quantityMatch = name.match(quantityOnlyPattern);

  if (quantityMatch) {
    const result = {
      quantity: parseInt(quantityMatch[1], 10),
    };
    console.log(`[parseProductName] "${name}" → qty only: ${result.quantity}`);
    return result;
  }

  // Only log when no pattern matched for debugging
  console.log(`[parseProductName] "${name}" → no match`);
  return {};
}

/**
 * Normalizes a photo size string for comparison
 * Handles various formats:
 * - "10x15" -> "10x15"
 * - "10 x 15" -> "10x15"
 * - "10 x 15 cm" -> "10x15"
 * - "10 x 15 cm sijaj" -> "10x15"
 * - "20 x 30 cm mat" -> "20x30"
 */
export function normalizePhotoSize(size: string): string {
  // Extract just the numbers and x, ignore everything else (cm, sijaj, mat, etc.)
  const match = size.match(/(\d+)\s*[xX×]\s*(\d+)/i);
  if (match) {
    const normalized = `${match[1]}x${match[2]}`.toLowerCase();
    console.log(`[normalizePhotoSize] "${size}" → "${normalized}"`);
    return normalized;
  }
  console.log(`[normalizePhotoSize] "${size}" → no match, returning as-is`);
  return size.toLowerCase().trim();
}

/**
 * Filters products based on user's order photo size and quantity
 * Returns products that match the photo size and have similar or matching quantities
 */
export function getRelevantProducts(
  products: GetProductDto[],
  userPhotoSize: string, // e.g., "10x15"
  userPhotoCount: number,
  currentLocale: string = "sl",
  options: {
    exactSizeMatch?: boolean; // If true, only match exact photo size
    quantityTolerance?: number; // Allow products with quantity within this tolerance (default: 20)
    maxResults?: number; // Maximum number of results to return
  } = {}
): ParsedProductInfo[] {
  const {
    exactSizeMatch = true,
    quantityTolerance = 20,
    maxResults = 5,
  } = options;

  const normalizedUserSize = normalizePhotoSize(userPhotoSize);
  const relevantProducts: ParsedProductInfo[] = [];

  console.log('[getRelevantProducts] Starting filter with:', {
    totalProducts: products.length,
    userPhotoSize,
    normalizedUserSize,
    userPhotoCount,
    currentLocale,
    options
  });

  for (const product of products) {
    // Skip inactive products
    if (!product.active) {
      console.log(`[getRelevantProducts] Skipping inactive product: ${product.id}`);
      continue;
    }

    // Get product name in current locale
    const nameMap = product.name as Record<string, string> | undefined;
    const productName = nameMap?.[currentLocale] || nameMap?.["en"] || "";

    if (!productName) {
      console.log(`[getRelevantProducts] No product name for locale ${currentLocale}, product ID: ${product.id}`);
      continue;
    }

    const parsed = parseProductName(productName);

    // A stored value wins over the parsed name, per field — exactly the same
    // rule productCardFacts uses for the card display. Do not diverge: a
    // stored capacity of 0 and an empty-string photoSize are treated as
    // absent, otherwise a half-filled admin form would suppress a value the
    // name could have supplied.
    const attrs = product as ProductWithAttributes;
    const storedCapacity =
      typeof attrs.capacity === "number" && attrs.capacity > 0
        ? attrs.capacity
        : null;
    const storedSize =
      typeof attrs.photoSize === "string" && attrs.photoSize.length > 0
        ? attrs.photoSize
        : null;
    const quantity = storedCapacity ?? parsed.quantity;
    const photoSize = storedSize ?? parsed.photoSize;

    // Check photo size match
    if (photoSize) {
      const normalizedProductSize = normalizePhotoSize(photoSize);
      const sizeMatches = normalizedProductSize === normalizedUserSize;

      // Skip products that don't match size if exactSizeMatch is enabled
      if (exactSizeMatch && !sizeMatches) {
        continue;
      }

      if (sizeMatches) {
        console.log(`[getRelevantProducts] ✓ Size match: ${productName} (${normalizedProductSize})`);
      }

      // If we have a photo size that matches, add it regardless of quantity
      // Quantity is only used for sorting, not filtering
      if (quantity !== undefined) {
        const quantityDiff = Math.abs(quantity - userPhotoCount);
        console.log(`[getRelevantProducts] ✓ ADDED: ${productName} (${sizeMatches ? 'size match' : 'diff size'}, qty: ${quantity}, diff: ${quantityDiff})`);
        relevantProducts.push({
          productId: product.id,
          quantity,
          photoSize,
          originalName: productName,
          sizeMatches,
        });
      } else {
        // Photo size present but no quantity specified - still relevant
        console.log(`[getRelevantProducts] ✓ ADDED: ${productName} (${sizeMatches ? 'size match' : 'diff size'}, no qty)`);
        relevantProducts.push({
          productId: product.id,
          photoSize,
          originalName: productName,
          sizeMatches,
        });
      }
    } else if (quantity !== undefined) {
      // No photo size in name, but has quantity - check if it's close
      const quantityDiff = Math.abs(quantity - userPhotoCount);

      if (quantityDiff <= quantityTolerance) {
        console.log(`[getRelevantProducts] ✓ ADDED: ${productName} (qty only: ${quantity})`);
        relevantProducts.push({
          productId: product.id,
          quantity,
          originalName: productName,
        });
      }
      // Don't log when not matching - too noisy
    }
    // Don't log when no photo size or quantity parsed - too noisy
  }

  console.log(`[getRelevantProducts] Found ${relevantProducts.length} relevant products before sorting`);

  // Sort by relevance:
  // 1. Exact size and quantity match
  // 2. Exact size match, no/any quantity
  // 3. Different size, with quantity
  // 4. Quantity match only, no size
  relevantProducts.sort((a, b) => {
    const aHasSize = !!a.photoSize;
    const bHasSize = !!b.photoSize;
    const aHasQuantity = a.quantity !== undefined;
    const bHasQuantity = b.quantity !== undefined;
    const aSizeMatches = a.sizeMatches || false;
    const bSizeMatches = b.sizeMatches || false;

    // Calculate score: size match is most important, then having both size & quantity
    const aScore = (aSizeMatches ? 1000 : 0) + (aHasSize ? 100 : 0) + (aHasQuantity ? 10 : 0);
    const bScore = (bSizeMatches ? 1000 : 0) + (bHasSize ? 100 : 0) + (bHasQuantity ? 10 : 0);

    if (aScore !== bScore) {
      return bScore - aScore; // Higher score first
    }

    // If both have quantity, prefer closer match
    if (aHasQuantity && bHasQuantity && a.quantity && b.quantity) {
      const aDiff = Math.abs(a.quantity - userPhotoCount);
      const bDiff = Math.abs(b.quantity - userPhotoCount);
      return aDiff - bDiff;
    }

    return 0;
  });

  // Return top results
  const topResults = relevantProducts.slice(0, maxResults);
  console.log(`[getRelevantProducts] Returning ${topResults.length} products after limiting to ${maxResults}:`, topResults);
  return topResults;
}
